'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent, DragEvent, Ref } from 'react';
import { useRef, useState } from 'react';
import { detectMimeType } from '@/entities/image-upload';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import { Loading } from '@ncai-axe/react-ui-v3';
import { useTranslations } from 'next-intl';
import styles from './UploadDropzone.module.scss';
const resolvePreviewSrc = (src: string): string => src.startsWith('blob:') || src.startsWith('data:') ? src : getImageProxyUrl({
  src
});
export type UploadRejectReason = 'type' | 'size' | 'dimension';
interface UploadDropzoneProps {
  onFile: (file: File) => void;
  onReject?: (reason: UploadRejectReason) => void;
  previewSrc?: string | null;
  disabled?: boolean;
  isUploading?: boolean;
  maxFileSizeBytes: number;
  acceptedMimeTypes: readonly string[];
  minImageWidth?: number;
  ref?: Ref<HTMLButtonElement>;
}
const isAcceptedType = (file: File, acceptedMimeTypes: readonly string[]): boolean => {
  const mime = detectMimeType(file);
  return mime != null && acceptedMimeTypes.includes(mime);
};
const isWithinSize = (file: File, maxFileSizeBytes: number): boolean => {
  return file.size <= maxFileSizeBytes;
};
const isAboveMinDimension = async (file: File, minImageWidth: number): Promise<boolean> => {
  try {
    const bitmap = await createImageBitmap(file);
    const ok = bitmap.width >= minImageWidth && bitmap.height >= minImageWidth;
    bitmap.close();
    return ok;
  } catch {
    return false;
  }
};
export const UploadDropzone = t0 => {
  const $ = _c(40);
  const {
    onFile,
    onReject,
    previewSrc,
    disabled: t1,
    isUploading: t2,
    maxFileSizeBytes,
    acceptedMimeTypes,
    minImageWidth,
    ref
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const isUploading = t2 === undefined ? false : t2;
  const t = useTranslations("intro");
  const inputRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  let t3;
  if ($[0] !== acceptedMimeTypes || $[1] !== maxFileSizeBytes || $[2] !== minImageWidth || $[3] !== onFile || $[4] !== onReject) {
    t3 = async file => {
      if (!file) {
        return;
      }
      if (!isAcceptedType(file, acceptedMimeTypes)) {
        onReject?.("type");
        return;
      }
      if (!isWithinSize(file, maxFileSizeBytes)) {
        onReject?.("size");
        return;
      }
      if (minImageWidth != null) {
        const dimensionOk = await isAboveMinDimension(file, minImageWidth);
        if (!dimensionOk) {
          onReject?.("dimension");
          return;
        }
      }
      onFile(file);
    };
    $[0] = acceptedMimeTypes;
    $[1] = maxFileSizeBytes;
    $[2] = minImageWidth;
    $[3] = onFile;
    $[4] = onReject;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleFile = t3;
  let t4;
  if ($[6] !== disabled) {
    t4 = () => {
      if (disabled) {
        return;
      }
      inputRef.current?.click();
    };
    $[6] = disabled;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleClick = t4;
  let t5;
  if ($[8] !== disabled) {
    t5 = event => {
      event.preventDefault();
      if (disabled) {
        return;
      }
      setIsActive(true);
    };
    $[8] = disabled;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleDragOver = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = _event => {
      setIsActive(false);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleDragLeave = t6;
  let t7;
  if ($[11] !== disabled || $[12] !== handleFile) {
    t7 = event_0 => {
      event_0.preventDefault();
      setIsActive(false);
      if (disabled) {
        return;
      }
      const file_0 = event_0.dataTransfer.files?.[0];
      handleFile(file_0);
    };
    $[11] = disabled;
    $[12] = handleFile;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleDrop = t7;
  let t8;
  if ($[14] !== handleFile) {
    t8 = event_1 => {
      const file_1 = event_1.target.files?.[0];
      handleFile(file_1);
      event_1.target.value = "";
    };
    $[14] = handleFile;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleInputChange = t8;
  const hasImage = Boolean(previewSrc);
  const t9 = hasImage ? styles.dropzoneWithImage : styles.dropzoneEmpty;
  const t10 = isActive ? styles.dropzoneActive : "";
  const t11 = disabled ? styles.dropzoneDisabled : "";
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t12 = [styles.dropzone, t9, t10, t11].filter(Boolean);
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const buttonClassName = t12.join(" ");
  let t13;
  if ($[20] !== isUploading || $[21] !== previewSrc || $[22] !== t) {
    t13 = previewSrc ? <div className={styles.previewWrap}><img src={resolvePreviewSrc(previewSrc)} alt={t("aiExperience.upload.previewAlt")} className={styles.previewImage} />{isUploading ? <div className={styles.uploadingOverlay} role="status" aria-live="polite"><Loading color="staticWhite" size={48} loadingMessage={t("aiExperience.upload.loading")} /></div> : null}</div> : <div className={styles.emptyContent}><img src="/intro/ic_upload.svg" alt="" className={styles.uploadIcon} /><p className={styles.primaryText}>{t("aiExperience.upload.prompt")}</p><p className={styles.secondaryText}>PNG, JPEG, WebP, HEIC<br />{t("aiExperience.upload.hint")}</p></div>;
    $[20] = isUploading;
    $[21] = previewSrc;
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== buttonClassName || $[25] !== disabled || $[26] !== handleClick || $[27] !== handleDragOver || $[28] !== handleDrop || $[29] !== ref || $[30] !== t13) {
    t14 = <button ref={ref} type="button" onClick={handleClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} disabled={disabled} className={buttonClassName}>{t13}</button>;
    $[24] = buttonClassName;
    $[25] = disabled;
    $[26] = handleClick;
    $[27] = handleDragOver;
    $[28] = handleDrop;
    $[29] = ref;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== acceptedMimeTypes) {
    t15 = acceptedMimeTypes.join(",");
    $[32] = acceptedMimeTypes;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== handleInputChange || $[35] !== t15) {
    t16 = <input ref={inputRef} type="file" accept={t15} className={styles.fileInput} onChange={handleInputChange} />;
    $[34] = handleInputChange;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <>{t14}{t16}</>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};